import {action, makeObservable, observable} from "mobx";
import {IOccasionService} from "../../../services/occasionService";
import Occasion from "./models/occasion";

/*
 * The interface for OccasionDomainStore.
 */
export interface IOccasionDomainStore {
  /**
   * The list of occasions.
   */
  occasions: Occasion[];

  /**
   * A GET call to get a list of occasions within a given date range.
   * If villageIds is not provided, it will return all occasions.
   */
  getOccasions: (
    startDate: string,
    endDate: string,
    villageIds?: string | undefined,
    abortController?: AbortController
  ) => Promise<void>;
  /**
   * A GET call to get a single occasion by ID.
   */
  getOccasion: (id: string, abortController?: AbortController) => Promise<any>;

  /**
   * A POST call to create a new occasion.
   */
  createOccasion: (createOccasion: Occasion) => Promise<void>;

  /**
   * A PUT call to update an existing occasion.
   */
  updateOccasion: (id: string, updateOccasion: Occasion) => Promise<void>;

  /**
   * A DELETE call to delete an occasion by ID.
   */
  deleteOccasion: (id: string) => Promise<void>;
}

/*
 * The domain store for Occasion.
 */
export class OccasionDomainStore implements IOccasionDomainStore {
  private readonly occasionService: IOccasionService;
  occasions: Occasion[] = [];

  constructor(occasionService: IOccasionService) {
    makeObservable(this, {
      occasions: observable,
      getOccasions: action,
      getOccasion: action,
      createOccasion: action,
      updateOccasion: action,
      deleteOccasion: action
    });
    this.occasionService = occasionService;
  }

  getOccasions = async (
    startDate: string,
    endDate: string,
    villageIds?: string | undefined,
    abortController?: AbortController
  ) => {
    const data = await this.occasionService.getOccasions(
      startDate,
      endDate,
      villageIds,
      abortController
    );

    this.occasions = data.map((d: any) => {
      return new Occasion(
        d.id,
        d.date,
        d.title,
        d.hasDrink,
        d.hasFood,
        d.isPrimary,
        d.villages
      );
    });
  };

  getOccasion = async (id: string, abortController?: AbortController) => {
    const existingOccasion = this.occasions.find((o) => o.id === id);
    if (existingOccasion) return existingOccasion.copyOccasion();

    const data = await this.occasionService.getOccasion(id, abortController);

    return new Occasion(
      data.id,
      data.date,
      data.title,
      data.hasDrink,
      data.hasFood,
      data.isPrimary,
      data.villages
    );
  };

  createOccasion = async (createOccasion: Occasion) => {
    await this.occasionService.createOccasion(createOccasion);

    this.occasions.push(createOccasion);
  };

  updateOccasion = async (id: string, updateOccasion: Occasion) => {
    await this.occasionService.updateOccasion(id, updateOccasion);

    const index = this.occasions.findIndex((o) => o.id === id);
    this.occasions[index] = updateOccasion;
  };

  deleteOccasion = async (id: string) => {
    await this.occasionService.deleteOccasion(id);

    this.occasions = this.occasions.filter((o) => o.id !== id);
  };
}
